import axios from "axios";
import { defineStore } from "pinia";
import { DosageSteadyState } from "@/service/models/common/query-dosage/parts/dosage-steady-state";
import { ComputationQuery } from "@/service/models/request/computation-query";
import { AdjustmentOptions } from "@/service/models/request/computation/adjustment-options";
import { ComputingOption } from "@/service/models/request/computation/computing-option";
import { ComputingTrait } from "@/service/models/request/computation/computing-traits/computing-trait";
import { CovariateValue } from "@/service/models/request/computation/covariate-value";
import { DosageTimeRange } from "@/service/models/request/computation/dosage-time-range";
import { Drug } from "@/service/models/request/computation/drug";
import { DrugSample } from "@/service/models/request/computation/drug-sample";
import { DrugSampleConcentration } from "@/service/models/request/computation/drug-sample-concentration";
import { DrugTarget } from "@/service/models/request/computation/drug-target";
import { DrugTreatment } from "@/service/models/request/computation/drug-treatment";
import { Request } from "@/service/models/request/computation/request";
import { ComputationResult } from "@/service/models/response/computation-result";
import { AdjustmentDosageTimeRange } from "@/service/models/response/computation/data-responses/parts/adjustment-dosage-time-range";
import { Status } from "@/service/models/response/computation/status";
import { DrugModelDetail } from "@/service/models/response/drug-model-detail";
import { DrugActiveMoiety } from "@/service/models/response/drug-model-detail/drug-active-moiety";
import { DrugCovariate } from "@/service/models/response/drug-model-detail/covariate/drug-covariate";
import { Target } from "@/service/models/response/drug-model-detail/target/target";
import { ComputationRequest } from "@/service/requests/computation-request";
import { DrugModelDetailRequest } from "@/service/requests/drug-model-detail-request";
import { deepCopy } from "@/utilities/cloning";
import { DatesRange } from "@/utilities/types/dates-range";
import { useMenuStateStore } from "./menu-state";
import { usePatientStore } from "./patient";
import { useReportStore } from "./report";

function makeDefaultAdjustmentOptions(): AdjustmentOptions
{
    return new AdjustmentOptions(
        AdjustmentOptions.BestCandidates.BEST_DOSAGE_PER_INTERVAL,
        AdjustmentOptions.LoadingDose.ALLOWED,
        AdjustmentOptions.RestPeriod.NO,
        AdjustmentOptions.SteadyStateTarget.STEADY_STATE,
        AdjustmentOptions.TargetExtraction.DEFINITION_IF_NO_INDIVIDUAL_TARGET,
        AdjustmentOptions.FormulationAndRouteSelected.LAST,
    );
}

export const useRequestDataStore = defineStore({
    id: 'requestDataStore',

    state()
    {
        return {
            selectedDrugName: null as string|null,              // May be filled before the actual model is selected
            selectedDrugModel: null as DrugModelDetail|null,    // Will be populated once the drug model is actually selected
            dosages: [] as DosageTimeRange[],
            covariates: new Map<string, CovariateValue[]>(),
            samples: [] as DrugSample[],
            targets: [] as DrugTarget[],
            adjustmentDate: null as Date|null,
            adjustmentOptions: makeDefaultAdjustmentOptions(),
            selectedAdjustmentDosages: [] as AdjustmentDosageTimeRange[],
        };
    },

    getters: {
        isDrugNameSelected(): boolean
        {
            return this.selectedDrugName !== null;
        },

        isDrugModelSelected(): boolean
        {
            return this.selectedDrugModel !== null;
        },

        hasCovariates(): boolean
        {
            for (const entry of this.covariates) {
                if (entry[0].length > 0) {
                    return true;
                }
            }

            return false;
        },

        hasDosages(): boolean
        {
            return this.dosages.length > 0;
        },

        hasSamples(): boolean
        {
            return this.samples.length > 0;
        },

        hasTargets(): boolean
        {
            return this.targets.length > 0;
        },

        hasAdjustment(): boolean
        {
            return this.selectedAdjustmentDosages.length > 0;
        },

        drugModelCovariatesString(): string
        {
            if (this.selectedDrugModel === null) {
                return "";
            }

            const names = [];

            for (const covariate of this.selectedDrugModel.model.covariates) {
                names.push(covariate.name);
            }

            return names.join(", ");
        },

        lastDosageInSelectedAdjustment(): AdjustmentDosageTimeRange|null
        {
            let lastDosage: AdjustmentDosageTimeRange|null = null;

            for (const dosage of this.selectedAdjustmentDosages) {
                if (lastDosage === null || dosage.start > lastDosage.start) {
                    lastDosage = dosage;
                }
            }

            return lastDosage;
        },

        computationParameterTypeForAdjustmentRequest(): ComputingOption.ParametersType
        {
            if (!this.hasCovariates) {
                return ComputingOption.ParametersType.POPULATION;
            }

            if (!this.hasSamples) {
                return ComputingOption.ParametersType.APRIORI;
            }

            return ComputingOption.ParametersType.APOSTERIORI;
        },
    },

    actions: {
        clearAll(): void
        {
            this.selectedDrugModel = null;

            this.clearDosages();
            this.clearCovariates();
            this.clearSamples();
            this.clearTargets();
            this.clearAdjustments();
        },

        deselectDrug()
        {
            const menuStateStore = useMenuStateStore();
            const reportStore = useReportStore();

            this.selectedDrugModel = null;

            // Clear all request data when changing the drugModel
            this.clearAll();    // requestDataStore may have watchers attached to it, so don't $reset it
            menuStateStore.$reset();
            reportStore.$reset();
        },

        async loadDrugModel(modelId: string)
        {
            this.selectedDrugModel = null;

            const drugModel = await new DrugModelDetailRequest(modelId).load() as DrugModelDetail;
            this.selectedDrugModel = drugModel;

            // Setup some drug-dependent data
            this.populateDataFromDrugAndPatientData();
        },

        populateDataFromDrugAndPatientData(): void
        {
            const patientStore = usePatientStore();

            if (this.selectedDrugModel === null) {
                return;
            }

            const setDefaultCovariateValue = (covariate: DrugCovariate, value: number, date: Date = new Date()) => {
                let currentDefaultValue = this.getNthCovariate(covariate, 0);

                if (currentDefaultValue === null) {
                    currentDefaultValue = this.makeCovariateValue(covariate, value, date);
                }
                else {
                    currentDefaultValue.value = value;
                    currentDefaultValue.date = date;
                }
            }

            // Default covariates
            for (const covariate of this.selectedDrugModel.model.covariates) {
                switch (covariate.type) {
                    case DrugCovariate.Type.AGE_IN_YEARS:
                        setDefaultCovariateValue(covariate, patientStore.getAgeInYears() ?? 0, patientStore.getLastBirthday() ?? new Date());
                        break;

                    case DrugCovariate.Type.AGE_IN_MONTHS:
                        setDefaultCovariateValue(covariate, patientStore.getAgeInMonths() ?? 0);
                        break;

                    case DrugCovariate.Type.AGE_IN_WEEKS:
                        setDefaultCovariateValue(covariate, patientStore.getAgeInWeeks() ?? 0);
                        break;

                    case DrugCovariate.Type.AGE_IN_DAYS:
                        setDefaultCovariateValue(covariate, patientStore.getAgeInDays() ?? 0);
                        break;

                    case DrugCovariate.Type.SEX:
                        setDefaultCovariateValue(covariate, patientStore.numericGender);
                        break;
                }
            }

            // Default targets
            for (const activeMoiety of this.selectedDrugModel.model.activeMoieties) {
                for (const target of activeMoiety.targets) {
                    this.targets.push(this.makeTarget(activeMoiety, target));
                }
            }

            // Adjustments options
            if (this.selectedDrugModel.model.defaultFormulationAndRoute.dosageStandardTreatment?.isFixedDuration) {
                this.adjustmentOptions.steadyStateTarget = AdjustmentOptions.SteadyStateTarget.WITHIN_TREATMENT;
            }
            else {
                this.adjustmentOptions.steadyStateTarget = AdjustmentOptions.SteadyStateTarget.STEADY_STATE;
            }
        },

        //
        // Dosage related actions
        //

        clearDosages(): void
        {
            this.dosages.splice(0, this.dosages.length);
        },

        removeNthDosage(dosageIndex: number): void
        {
            this.dosages.splice(dosageIndex, 1);
        },

        getDosageAt(date: Date): DosageTimeRange|null
        {
            let steadyStateDosage: DosageTimeRange|null = null;

            for (const dosage of this.dosages) {
                if (dosage.dosage.dosageRepeat instanceof DosageSteadyState) {
                    steadyStateDosage = dosage;
                    continue;
                }

                // If we find a non-steady state dosage that contains the searched date, it is the one we are looking for
                if (dosage.isDateWithin(date)) {
                    return dosage;
                }
            }

            return steadyStateDosage;
        },

        expandToCoverDosageCycle(range: DatesRange): DatesRange
        {
            const startDosage = this.getDosageAt(range.from);
            const endDosage = this.getDosageAt(range.to);
            let start = range.from;
            let end = range.to;

            if (startDosage !== null) {
                start = startDosage.getPreviousDoseDateFrom(start);
            }

            if (endDosage !== null) {
                end = endDosage.getNextDoseDateFrom(end);
            }

            return new DatesRange(start, end);
        },

        //
        // Covariates related actions
        //

        clearCovariates(): void
        {
            this.covariates.clear();
        },

        makeCovariateValue(covariate: DrugCovariate, value: number, date: Date): CovariateValue
        {
            const covariateValue = CovariateValue.makeFromCovariate(covariate, value, date);

            this.addCovariateValue(covariateValue);

            return covariateValue;
        },

        addCovariateValue(value: CovariateValue): void
        {
            let covariatesHistory = this.covariates.get(value.id);

            if (covariatesHistory === undefined) {
                covariatesHistory = [];
                this.covariates.set(value.id, covariatesHistory);
            }

            covariatesHistory.push(value);
        },

        removeCovariateByUuid(covariateId: string, valueUuid: string): void
        {
            const historyArray = this.covariates.get(covariateId) ?? [];

            for (let i = 0; i < historyArray.length; i++) {
                if (historyArray[i].uuid === valueUuid) {
                    historyArray.splice(i, 1);
                    return;
                }
            }
        },

        getNthCovariate(covariate: DrugCovariate, i: number): CovariateValue|null
        {
            const covariatesHistory = this.covariates.get(covariate.id);

            if (covariatesHistory === undefined) {
                return null;
            }

            return covariatesHistory[i] ?? null;
        },

        getCovariateValueAt(covariate: DrugCovariate, date: Date): number
        {
            return this.getCovariateAt(covariate.id, date)?.value ?? covariate.covariateValue.value;
        },

        getCovariateAt(covariateId: string, date: Date): CovariateValue|null
        {
            const covariateHistory = this.covariates.get(covariateId);

            // If there is no history for this covariate, return null
            if (covariateHistory === undefined) {
                return null;
            }

            // Find the latest covariate that is not in the future
            let latestCovariate: CovariateValue|null = null;

            for (const covariate of covariateHistory) {
                // Skip future values
                if (covariate.date > date) {
                    continue;
                }

                if (latestCovariate === null || covariate.date > latestCovariate.date) {
                    latestCovariate = covariate;
                }
            }

            return latestCovariate;
        },

        getCovariatesAt(date: Date): CovariateValue[]
        {
            const result: CovariateValue[] = [];

            for (const covariateEntry of this.covariates) {
                const covariateValue = this.getCovariateAt(covariateEntry[0], date);

                // If a current value is found in the history, add it to the result
                if (covariateValue !== null) {
                    result.push(covariateValue);
                }
            }

            return result;
        },

        //
        // Samples related actions
        //

        clearSamples(): void
        {
            const menuStateStore = useMenuStateStore();
            menuStateStore.measuresValidated = false;

            this.samples.splice(0, this.samples.length);
        },

        removeNthSample(sampleIndex: number): void
        {
            const menuStateStore = useMenuStateStore();

            this.samples.splice(sampleIndex, 1);

            if (!this.hasSamples) {
                menuStateStore.measuresValidated = false;
            }
        },

        makeSample(analyteId: string, id = "", date = new Date(), valueInUgPerLiter = 0.0): DrugSample
        {
            const concentration = new DrugSampleConcentration(analyteId, valueInUgPerLiter, "ug/l");
            const sample = new DrugSample(id, date);

            sample.concentrations.push(concentration);

            return sample;
        },

        //
        // Targets related actions
        //

        clearTargets(): void
        {
            this.targets.splice(0, this.targets.length);
        },

        removeNthTarget(targetIndex: number): void
        {
            this.targets.splice(targetIndex, 1);
        },

        makeTarget(activeMoiety: DrugActiveMoiety|null, targetTemplate: Target|null): DrugTarget
        {
            return new DrugTarget(
                activeMoiety?.id ?? "",
                targetTemplate?.type ?? Target.Type.RESIDUAL,
                targetTemplate?.unit ?? "",
                targetTemplate?.min.value ?? 0,
                targetTemplate?.best.value ?? 0,
                targetTemplate?.max.value ?? 0,
                targetTemplate?.inefficacyAlarm.value ?? null,
                targetTemplate?.toxicityAlarm.value ?? null,
                targetTemplate?.times?.min ?? null,
                targetTemplate?.times?.best ?? null,
                targetTemplate?.times?.max ?? null,
                targetTemplate?.mic?.unit ?? "ug/l",
                targetTemplate?.mic?.value.value ?? null,
            );
        },

        //
        // Adjustments related actions
        //

        clearAdjustments(): void
        {
            this.adjustmentDate = null;
            this.adjustmentOptions = makeDefaultAdjustmentOptions();
            this.selectedAdjustmentDosages.splice(0);
        },

        removeNthSelectedAdjustment(adjustmentIndex: number): void
        {
            this.selectedAdjustmentDosages.splice(adjustmentIndex, 1);
        },

        //
        // Utilities to build and run computations
        //

        makeComputationRequest(computingTrait: ComputingTrait): Request
        {
            if (this.selectedDrugModel === null) {
                throw new Error("Cannot run query if no drug is selected");
            }

            return new Request(
                this.selectedDrugModel.model.id,
                this.selectedDrugModel.model.modelId,
                computingTrait
            );
        },

        getDosagesForQuery(forAdjustment: boolean): Array<DosageTimeRange|AdjustmentDosageTimeRange>
        {
            // If we are asked for dosages for a normal query (not an adjustment query), simply return
            // the configured dosages
            if (!forAdjustment) {
                return this.dosages;
            }

            // Otherwise, trim all the dosages to the adjustment date and append the adjusted dosages
            const dosages: Array<DosageTimeRange|AdjustmentDosageTimeRange> = [];

            for (const dosage of this.dosages) {
                const dosageClone = deepCopy(dosage);
                
                if (this.adjustmentDate !== null && dosageClone.end > this.adjustmentDate) {
                    dosageClone.end = this.adjustmentDate;
                }

                dosages.push(dosageClone);
            }

            dosages.push(...this.selectedAdjustmentDosages);

            return dosages;
        },

        makeComputationQuery(requests: Array<Request|null>, requestAdjustments = false): ComputationQuery
        {
            if (this.selectedDrugModel === null) {
                throw new Error("Cannot run query if no drug is selected");
            }

            const queryData = new ComputationQuery();

            // If the query concern some adjustments predictions/percentiles, add a prefix to the query id
            if (requestAdjustments) {
                queryData.queryId = "adj_" + queryData.queryId;
            }

            // Add the current drug model to the request
            const requestDrug = new Drug(
                this.selectedDrugModel.model.id,
                this.selectedDrugModel.head.activeSubstances[0],
                "",     // Actually there is no brand in the drugModel data
                this.selectedDrugModel.head.atcs[0],
                new DrugTreatment(),
            );
            queryData.drugs = [ requestDrug ];

            // Add dosages to the request
            requestDrug.treatment.dosageTimeRanges = this.getDosagesForQuery(requestAdjustments);

            // Add covariates to the request
            queryData.covariates = [];
            for (const covariatesEntry of this.covariates) {
                for (const covariate of covariatesEntry[1]) {   // covariatesEntry[1] are map values
                    if (covariate.isAge()) {
                        // This way of dealing with the age is not very elegant, but required as the value of a CovariateValue is a number.
                        // It should at some stage be a string so we could store the birthdate.
                        const patientStore = usePatientStore();
                        const newCovariate  = new CovariateValue('birthdate', patientStore.birthDate ?? new Date(), 0, '-', covariate.type, covariate.nature);
                        queryData.covariates.push(newCovariate);

                        continue;   // As we already sent it
                    }

                    queryData.covariates.push(covariate);
                }
            }

            // Add samples to the request
            requestDrug.samples = this.samples;

            // Add targets to the request
            requestDrug.targets = this.targets;

            // Add things to ask for to the request
            for (const request of requests) {
                if (request !== null) {
                    queryData.requests.push(request);
                }
            }

            return queryData;
        },

        makeApiRequest(queryData: ComputationQuery): ComputationRequest
        {
            return new ComputationRequest(queryData);
        },

        waitForApiRequest(apiRequest: ComputationRequest): Promise<ComputationResult>
        {
            return new Promise((resolve) => {
                apiRequest.load()
                    // If the request finishes successfully, simply forward the result in the forwarded promise
                    .then((response) => {
                        resolve(response as ComputationResult);
                    })
                    .catch((e) => {
                        // Handle cancellations by resolving the promise with a dummy response
                        if (axios.isCancel(e)) {
                            resolve(new ComputationResult(
                                apiRequest.queryId,
                                new Status(99, "DummyClientError", "Request cancelled", ""),
                                []
                            ));
                            return;
                        }

                        // Otherwise forward the exception
                        throw e;
                    });
            });
        },
    },
});
